import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Callout,
  Card,
  Code,
  Dialog,
  Flex,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionAlertDialog } from "components";
import { useDisconnectMcp, useGetMcpStatus } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { ClaudeMark } from "./ClaudeMark";

export const CLAUDE_DISCONNECT_NOTE =
  "Claude may still show this connector as connected. Remove it in Claude yourself if you want it gone from there too.";

export function ClaudeSetupDialog({
  isOpen,
  onOpenChange,
  userProfile,
  isConnected,
  mcpUrl,
  connectedBy,
}) {
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const disconnectMcp = useDisconnectMcp();
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const isOwner = userProfile?.user_role === "Owner";

  const displayUrl = useMemo(
    () => mcpUrl || "Ask your admin to set MCP_PUBLIC_URL on the API.",
    [mcpUrl]
  );

  const copyUrl = async () => {
    if (!mcpUrl) {
      setToastMessage("Connector URL is not configured yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setToastMessage("Could not copy the URL. Select it and copy manually.");
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectMcp.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });
      queryClient.invalidateQueries(["mcp-status", userProfile.organisation_id]);
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message || "Could not disconnect Claude. Please try again."
      );
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Content maxWidth="520px">
          <Flex direction="column" gap="4">
            <Flex align="center" gap="3">
              <ClaudeMark size={36} />
              <Flex direction="column" gap="1">
                <Dialog.Title mb="0">Connect Claude to BConnect</Dialog.Title>
                <Text size="2" color="gray">
                  Ask read-only questions about guests, Wi-Fi, reviews, feedback, campaigns, and venues.
                </Text>
              </Flex>
            </Flex>

            {isConnected ? (
              <Callout.Root color="green" variant="soft">
                <Callout.Text>
                  Connected{connectedBy ? ` by ${connectedBy}` : ""}. Claude can read this organisation until you disconnect.
                </Callout.Text>
              </Callout.Root>
            ) : null}

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">How it works</Text>
              <Text size="2" color="gray">
                Connection starts in Claude. Paste the BConnect MCP URL there, sign in when prompted,
                and approve one organisation. BConnect only stores tokens for the organisation you approve.
              </Text>
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">1. Copy your connector URL</Text>
              <Flex gap="2" align="center" width="100%">
                <TextField.Root
                  value={displayUrl}
                  readOnly
                  size="2"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Button size="2" variant="soft" color="gray" onClick={copyUrl} disabled={!mcpUrl}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </Flex>
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">2. Add it in Claude</Text>
              <Text size="2" color="gray">
                In Claude, add a custom connector / MCP server and paste the URL above
                (including the trailing <Code>/mcp/</Code>).
              </Text>
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">3. Sign in and approve</Text>
              <Text size="2" color="gray">
                Claude opens BConnect. Sign in, choose one organisation, then ask questions such as
                “What changed this week?” or “How was Wi-Fi at our main venue?”
              </Text>
            </Flex>

            <Callout.Root color="gray" variant="soft">
              <Callout.Text>
                Access is read-only. Only the organisation you approve is visible.
              </Callout.Text>
            </Callout.Root>

            <Flex justify="between" gap="3" mt="2">
              <Dialog.Close>
                <Button size="2" variant="soft" color="gray">
                  Close
                </Button>
              </Dialog.Close>
              {isConnected ? (
                <Button
                  size="2"
                  color="red"
                  variant="soft"
                  onClick={() => {
                    if (!isOwner) {
                      setToastMessage("Only owners can disconnect Claude.");
                      return;
                    }
                    setConfirmOpen(true);
                  }}
                >
                  Disconnect
                </Button>
              ) : null}
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content maxWidth="440px">
          <Dialog.Title>Disconnect Claude?</Dialog.Title>
          <Flex direction="column" gap="3" mt="2">
            <Text size="2" color="gray">
              This revokes BConnect access for Claude for this organisation right away.
            </Text>
            <Text size="2" color="gray">
              {CLAUDE_DISCONNECT_NOTE}
            </Text>
            <Flex justify="end" gap="2">
              <Button
                size="2"
                variant="soft"
                color="gray"
                onClick={() => setConfirmOpen(false)}
                disabled={isDisconnecting}
              >
                Cancel
              </Button>
              <Button
                size="2"
                color="red"
                onClick={handleDisconnect}
                loading={isDisconnecting}
              >
                Disconnect
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

export function BrowseClaudeCard({ userProfile }) {
  const { data } = useGetMcpStatus(userProfile?.organisation_id);
  const isConnected = data?.data?.is_active === true;
  const mcpUrl = data?.data?.mcp_url || "";
  const connectedBy = data?.data?.connected_by || "";
  const [setupOpen, setSetupOpen] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);

  const openSetup = () => {
    if (userProfile?.user_role === "Owner" || userProfile?.user_role === "Manager") {
      setSetupOpen(true);
      return;
    }
    setPermissionOpen(true);
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          minWidth: 0,
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <ClaudeMark />
          <Flex direction="column" justify="center" align="start" gap="1">
            <Text size="4" weight="medium">
              Claude
            </Text>
            <Text size="1" color="gray">
              Ask questions about your BConnect data
            </Text>
          </Flex>
        </Flex>
        <Flex align="center" gap="3">
          {isConnected ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : null}
          <Button onClick={openSetup} size="2" variant="soft" color="gray">
            {isConnected ? "Manage" : "Set up"}
          </Button>
        </Flex>
      </Card>

      {setupOpen ? (
        <ClaudeSetupDialog
          isOpen={setupOpen}
          onOpenChange={setSetupOpen}
          userProfile={userProfile}
          isConnected={isConnected}
          mcpUrl={mcpUrl}
          connectedBy={connectedBy}
        />
      ) : null}

      <PermissionAlertDialog
        isOpen={permissionOpen}
        onOpenChange={setPermissionOpen}
        message="Only owners and managers can view Claude setup"
      />
    </>
  );
}
